import React, { memo, useCallback, useMemo, useRef, useState, useEffect } from "react"
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  Flex,
  Heading,
  VStack,
  Box,
  Text,
  Grid,
  Divider,
  RadioGroup,
  Radio,
  Stack,
} from "@chakra-ui/react"

import { useConfigContext } from "@app/providers/config"
import { useCart } from "@app/hooks/useCart"
import { useForm } from "@app/hooks/useForm"
import { graphql, useStaticQuery } from "gatsby"
import { FormButton } from "@app/components/Form/FormButton"
import { FormDate } from "@app/components/Form/FormDate"
import { FormInput } from "@app/components/Form/FormInput"
import { FormTextarea } from "@app/components/Form/FormTextarea"
import { ProductGiftCardDenominations } from "@app/components/Product/Giftcard/ProductGiftCardDenominations"
import { Icon } from "@app/components/Icon"
import type { SelectedOptionProps, ProductVariantProps } from "@root/types/global"
import { useGlobalContext } from "@app/providers/global"
import { validate } from "react-email-validator"
import { SelectableOption } from "@app/components/Product/Form/ProductFormTypes"

export type FormData = {
  email: string
  date: string
  message: string
  name: string
}

type Props = {
  handleToggle: () => void
  isOpen: boolean
  selectableOptions: SelectableOption[]
  selectedOptions: Array<SelectedOptionProps>
  variant?: ProductVariantProps
  handleOption: (option: { name: string; value: string }) => void
}

const INITIAL_STATE = {
  _gift_recipient_name: "",
  _gift_sender_name: "",
  _gift_recipient_email: "",
  _gift_recipient_delivery_date: "",
  _gift_recipient_message: "",
  _gift_recipient_item_ref: "",
}

export const ProductGiftcardForm: React.FC<Props> = memo(
  ({ handleToggle, isOpen, selectableOptions, selectedOptions, variant, handleOption }) => {
    const { giftcard } = useStaticQuery<GatsbyTypes.ProductGiftcardFormQuery>(graphql`
      query ProductGiftcardForm {
        giftcard: sanityPageGiftcard {
          additionalGiftAddToCart
          additionalGiftcard
          additionalGiftcardSendername
          additionalGiftcardName
          additionalGiftcardEmail
          additionalGiftcardDate
          additionalGiftcardDateNote
          additionalGiftcardMessage
          additionalAmountErrorMessage
          additionalFormHeading
          additionalToHeading
          additionalMessageHeading
          additionalFromHeading
          additionalValueLabel
        }
      }
    `)
    const {
      settings: {
        product: { giftCardSortOrder, giftCardDenomination, giftCardDesign },
      },
    } = useConfigContext()
    const { additionalClose, additionalCancel, additionalLater, additionalNow } = useGlobalContext()
    const initialRef = useRef<HTMLInputElement>(null)
    const { data, handleChange } = useForm(INITIAL_STATE)
    const [errors, setErrors] = useState<string[]>([])

    const selectedDenomination = variant?.selectedOptions.find(option => option.name === giftCardDenomination)?.value || ""

    const { addToCart, loading: cartLoading } = useCart()
    const [send, setSend] = useState("now")

    const [denominationValueValid] = useState(true)

    const isDisabled = useMemo(() => {
      const hasEmptyFields = Object.entries(data)
        .filter(([key]) => key !== "_gift_recipient_delivery_date")
        .some(([value]) => !value)

      if (send === "now") {
        return hasEmptyFields
      }
      return hasEmptyFields || !data._gift_recipient_delivery_date
    }, [data, send])

    const giftRecipientItemRef = useMemo(() => {
      return variant?.selectedOptions?.find((item: { name: string; value: string }) => item.name === giftCardDesign)?.value || ""
    }, [variant?.selectedOptions, giftCardDesign])
    data._gift_recipient_item_ref = giftRecipientItemRef

    const nextDay = new Date()
    nextDay.setDate(nextDay.getDate() + 1)
    nextDay.setMinutes(0)
    const oneYearFromNow = new Date()
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1)

    const errorRef = useRef(null)

    const handleSubmit = useCallback(
      async event => {
        event.preventDefault()

        if (isDisabled || !variant?.id) return

        const emailValid = validate(data?._gift_recipient_email)

        const newErrors: string[] = []
        Object.entries(data).forEach(([key, value]) => {
          if (!value && key !== "_gift_recipient_delivery_date") {
            newErrors.push(`Please enter ${key.replace("_gift_", "").replace("_", " ")}`)
          }
        })

        if (!emailValid) newErrors.push("Please enter a valid email address")

        setErrors(newErrors)

        if (newErrors.length > 0) {
          // Scroll to error message field if errors exist
          setTimeout(() => {
            errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
          }, 100)
          return
        }

        const attributes = Object.entries({
          ...data,
          _gift_recipient_delivery_date: send === "now" ? new Date() : data._gift_recipient_delivery_date,
        })
          .filter(attribute => attribute[1])
          .map(([key, value]) => ({
            key,
            value:
              key.includes("date") && value
                ? `${`0${value.getMonth() + 1}`.slice(-2)}/${`0${value.getDate()}`.slice(-2)}/${value.getFullYear()}`
                : value,
          }))
          .sort(({ key: keyA }, { key: keyB }) => giftCardSortOrder.indexOf(keyA) - giftCardSortOrder.indexOf(keyB))

        const timestampAttribute = { key: "_timestamp", value: new Date().getTime().toString() }

        await addToCart(variant?.id, 1, [...attributes, timestampAttribute])
        handleToggle()
      },
      [addToCart, variant, data, handleToggle, isDisabled, giftCardSortOrder, send]
    )

    useEffect(() => {
      if (!cartLoading) {
        Object.keys(INITIAL_STATE).forEach(key => {
          handleChange({ target: { name: key, value: "" } } as any)
        })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartLoading])

    return (
      <Drawer initialFocusRef={initialRef} isOpen={isOpen} placement="right" size="xl" onClose={handleToggle} returnFocusOnClose={false}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader
            as={VStack}
            alignItems="flex-start"
            bg="white"
            flexDirection="row"
            justifyContent="space-between"
            px={[4.25, 5.5, 11]}
            pt={[4.25, 5.5, 11]}
            pb={[9, 2.5, 2]}
          >
            <Heading
              as="h3"
              color="grey.coal"
              lineHeight={1.35}
              mt={[2, 4.75]}
              textAlign="left"
              w={{ base: "70%", md: "full" }}
              fontWeight={500}
              letterSpacing="generous"
              textTransform="initial"
              fontSize="3xl"
            >
              {giftcard?.additionalGiftcard}
            </Heading>
            <Flex alignItems="baseline" alignSelf="flex-start" justifyContent="flex-end">
              <FormButton onClick={handleToggle} title={additionalClose} aria-label={additionalClose} p={2} variant="ghost">
                <Icon name="close" width={5} />
              </FormButton>
            </Flex>
          </DrawerHeader>
          <DrawerBody
            as={VStack}
            alignItems="center"
            justifyContent="flex-start"
            bg="white"
            pt={1.5}
            pb={[6.25, 7.5, 12.5]}
            px={[6.25, 7.5, 12.5]}
          >
            <VStack
              as="form"
              onSubmit={handleSubmit}
              alignItems="stretch"
              justifyContent="flex-start"
              maxW="full"
              spacing={{ base: 18.75, sm: 7.5 }}
              w="full"
            >
              <VStack alignItems="stretch" justifyContent="flex-start" spacing={3.75}>
                <Box ref={initialRef}>
                  <Text fontSize="16" lineHeight="5" mb="4" letterSpacing="generous" color="grey.coal">
                    {giftcard?.additionalValueLabel} {selectedDenomination}
                  </Text>
                  <Grid width="100%" rowGap="3" gap="4" mb="4" gridTemplateColumns={["repeat(4, 1fr)", "repeat(7, 1fr)"]}>
                    <ProductGiftCardDenominations
                      selectedOptions={selectedOptions}
                      selectableOptions={selectableOptions}
                      handleOption={handleOption}
                    />
                  </Grid>
                  {!denominationValueValid && (
                    <Text variant="text12" fontWeight={600} color="brand.red" mt="1">
                      {giftcard?.additionalAmountErrorMessage}
                    </Text>
                  )}
                  <Divider borderBottomColor="grey.fog" my={8} />
                </Box>
                <Flex direction="column" alignItems="left" gap={[8, 6]}>
                  <Box>
                    <Text fontSize="16" lineHeight="5" mb="4" letterSpacing="generous" color="grey.coal" fontWeight={500}>
                      {giftcard?.additionalFormHeading}
                    </Text>
                    <Text fontSize="14" lineHeight="5" mb="4" letterSpacing="generous" color="grey.coal">
                      {giftcard?.additionalToHeading}
                    </Text>
                    <Flex direction="column" alignItems="left" gap={6}>
                      <FormInput
                        isDisabled={cartLoading}
                        errors={errors?.filter(error => error.includes("recipient name"))}
                        onChange={handleChange}
                        name="_gift_recipient_name"
                        placeholder={giftcard?.additionalGiftcardName}
                        value={data?._gift_recipient_name}
                        borderRadius="8px"
                        borderColor={
                          errors.some(error => error.includes("recipient name"))
                            ? "var(--chakra-colors-brand-error) !important"
                            : "grey.mid"
                        }
                        labelColor={errors.some(error => error.includes("recipient name")) ? "brand.error" : "grey.coal"}
                        errorAlignment="flex-start"
                        errorIcon={false}
                        reference={errorRef}
                      />
                      <FormInput
                        autoComplete="email"
                        isDisabled={cartLoading}
                        errors={errors?.filter(error => error.includes("valid"))}
                        onChange={handleChange}
                        name="_gift_recipient_email"
                        placeholder={giftcard?.additionalGiftcardEmail}
                        type="email"
                        value={data?._gift_recipient_email}
                        borderRadius="8px"
                        borderColor={
                          errors.some(error => error.includes("valid")) ? "var(--chakra-colors-brand-error) !important" : "grey.mid"
                        }
                        labelColor={errors.some(error => error.includes("valid")) ? "brand.error" : "grey.coal"}
                        errorAlignment="flex-start"
                        errorIcon={false}
                      />
                    </Flex>
                  </Box>
                  <Box>
                    <Text fontSize="14" lineHeight="5" mb="4" letterSpacing="generous" color="grey.coal">
                      {giftcard?.additionalMessageHeading}
                    </Text>
                    <FormTextarea
                      isDisabled={cartLoading}
                      errors={errors?.filter(error => error.includes("message"))}
                      onChange={handleChange}
                      maxLength={100}
                      name="_gift_recipient_message"
                      placeholder={giftcard?.additionalGiftcardMessage}
                      value={data?._gift_recipient_message}
                      borderRadius="8px"
                      borderColor={
                        errors.some(error => error.includes("message")) ? "var(--chakra-colors-brand-error) !important" : "grey.mid"
                      }
                      labelColor={errors.some(error => error.includes("message")) ? "brand.error" : "grey.coal"}
                      errorIcon={false}
                      errorAlignment="flex-start"
                    />
                  </Box>
                  <Box>
                    <Text fontSize="14" lineHeight="5" mb="4" letterSpacing="generous" color="grey.coal">
                      {giftcard?.additionalFromHeading}
                    </Text>
                    <FormInput
                      autoComplete="given-name"
                      isDisabled={cartLoading}
                      errors={errors?.filter(error => error.includes("sender"))}
                      onChange={handleChange}
                      name="_gift_sender_name"
                      placeholder={giftcard?.additionalGiftcardSendername}
                      value={data?._gift_sender_name}
                      borderRadius="8px"
                      borderColor={
                        errors.some(error => error.includes("sender")) ? "var(--chakra-colors-brand-error) !important" : "grey.mid"
                      }
                      labelColor={errors.some(error => error.includes("sender")) ? "brand.error" : "grey.coal"}
                      errorAlignment="flex-start"
                      errorIcon={false}
                    />
                  </Box>
                  <Flex justifyContent="space-between" mt="5" flexDirection={{ base: "column", sm: "row" }}>
                    <Text fontSize="16" lineHeight="shorter" mb="5" fontWeight={500} color="grey.coal">
                      {giftcard?.additionalGiftcardDateNote}
                    </Text>

                    <RadioGroup value={send} onChange={value => setSend(value)}>
                      <Stack spacing="2.5" direction="row">
                        <Radio colorScheme="customColor" value="now" verticalAlign="middle">
                          {additionalNow}
                        </Radio>
                        <Radio colorScheme="customColor" value="later" verticalAlign="middle">
                          {additionalLater}
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </Flex>
                  {send === "later" && (
                    <Box mb="5">
                      <FormDate
                        future
                        minDate={nextDay}
                        maxDate={oneYearFromNow}
                        isDisabled={cartLoading}
                        onChange={handleChange}
                        name="_gift_recipient_delivery_date"
                        placeholder={giftcard?.additionalGiftcardDate}
                        value={data?._gift_recipient_delivery_date}
                        borderRadius="8px"
                        labelColor="grey.coal"
                      />
                    </Box>
                  )}
                </Flex>
              </VStack>
              <Grid alignItems="flex-end" justifyContent="flex-start" gridGap={3.75} gridTemplateColumns="1fr 1fr">
                <FormButton
                  size="md"
                  title={additionalCancel}
                  aria-label={additionalCancel}
                  type="button"
                  onClick={handleToggle}
                  variant="secondary"
                  width="50%"
                  borderRadius="8px"
                  textTransform="initial"
                >
                  {additionalCancel}
                </FormButton>
                <FormButton
                  isDisabled={isDisabled}
                  isLoading={cartLoading}
                  size="md"
                  title={giftcard?.additionalGiftAddToCart}
                  aria-label={giftcard?.additionalGiftAddToCart}
                  type="submit"
                  value={data?.colour}
                  variant="pdpAddToCart"
                  width="50%"
                  mt={0}
                >
                  {giftcard?.additionalGiftAddToCart}
                </FormButton>
              </Grid>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    )
  }
)
